import { useParams, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { resolveNotificationRoute } from "app/router";
import { Spinner } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";

type State =
    | { kind: "resolving" }
    | { kind: "not_found" }
    | { kind: "target_deleted" }
    | { kind: "error" };

/**
 * Backing component for /n/{token}. Hits the resolver, then either
 * navigates to the canonical entity URL or shows a friendly status
 * screen. Logged-out visits are bounced to /login by the root guard,
 * which preserves the token path through the auth flow.
 *
 * `lastResolvedRef` keys the effect on token rather than firing once
 * per mount — TanStack Router can reuse this same component across
 * subsequent /n/{otherToken} navigations, and a one-time guard would
 * leave later tokens unresolved.
 *
 * Cast on `router.navigate({ to: ... as "/" })` mirrors what Login does
 * for `redirectManager.consume()`: the resolver returns an arbitrary
 * SPA path, which the typed router would otherwise reject.
 */
export const ResolveNotification = () => {
    const { token } = useParams({ from: resolveNotificationRoute.id });
    const router = useRouter();
    const resolve = trpc.notifications.resolveByToken.useMutation();
    const currentUser = useCurrentUser();
    const [state, setState] = useState<State>({ kind: "resolving" });
    const lastResolvedRef = useRef<string | null>(null);

    useEffect(() => {
        if (lastResolvedRef.current === token) return;
        lastResolvedRef.current = token;
        setState({ kind: "resolving" });
        resolve.mutate(
            { token },
            {
                onSuccess: (result) => {
                    if (result.status === "ok") {
                        const url = new URL(result.url);
                        const target = url.pathname + url.search;
                        router.navigate({ to: target as "/", replace: true });
                        return;
                    }
                    setState({ kind: result.status });
                },
                onError: () => setState({ kind: "error" }),
            },
        );
    }, [token, router, resolve]);

    if (state.kind === "resolving") {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="xl" />
            </div>
        );
    }

    const message =
        state.kind === "not_found"
            ? "This notification doesn't exist, or you don't have access to it."
            : state.kind === "target_deleted"
              ? "The item this notification pointed to is no longer available."
              : "Something went wrong opening this notification.";

    // For not_found we can't tell "no such notification" from "belongs to a
    // different account" apart — surfacing who the visitor is signed in as lets
    // them spot the common cause: they're logged into the wrong account.
    const signedInAs = currentUser?.name
        ? `${currentUser.name} (${currentUser.email})`
        : currentUser?.email;

    return (
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-24 text-center">
            <h1 className="text-xl font-semibold text-gray-800">Notification unavailable</h1>
            <p className="text-gray-600">{message}</p>
            {state.kind === "not_found" && signedInAs && (
                <p className="text-sm text-gray-500">Signed in as {signedInAs}</p>
            )}
            <a href="/" className="text-blue-600 underline">
                Go home
            </a>
        </div>
    );
};
